import type { IPickPageSource, IPickPageSourceParams } from '../../types';
import type { IThemePage } from '~/stores/themePage';

import { toPickPageHomeItem, toPickPageSearchSubject } from '../../utils';
import { toRenderedHomeTemplateSuffix } from '~/utils';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

const HOME_TITLE = 'Homepage';

export interface IPickPageHomeSourceParams extends IPickPageSourceParams {
  /** The theme's pages, to resolve the home template serving `/` — see `toRenderedHomeTemplateSuffix`. */
  themePages?: IThemePage[];
}

/** The homepage is a single fixed row, so this source filters it locally instead of querying. */
export const usePickPageHomeSource = ({
  searchValue,
  isEnabled,
  templateAssetKeys,
  themePages,
}: IPickPageHomeSourceParams): IPickPageSource => {
  const { t } = useTranslation();

  const items = useMemo(() => {
    if (!isEnabled) return [];

    const title = t(HOME_TITLE);
    const item = toPickPageHomeItem({
      title,
      templateSuffix: toRenderedHomeTemplateSuffix({ themePages, templateAssetKeys }),
    });
    const subject = toPickPageSearchSubject(searchValue).toLowerCase();
    const isMatched =
      !subject || title.toLowerCase().includes(subject) || item.url.includes(subject);

    return isMatched ? [item] : [];
  }, [t, searchValue, isEnabled, themePages, templateAssetKeys]);

  return {
    items,
    isLoading: false,
    isError: false,
    isSourceEmpty: false,
    isFetchingNextPage: false,
    hasNextPage: false,
    handleFetchNextPage: () => undefined,
    handleRefresh: () => undefined,
  };
};
